/* global google */
import "./App.scss";
import { useState, useEffect } from "react";
import { default as axios } from "./axiosConfig";
import { jwtDecode } from "jwt-decode";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { GoogleOAuthProvider, googleLogout } from "@react-oauth/google";
// Components
import ForgotPasswordPage from "./Components/ForgotPasswordPage";
import ResetPasswordPage from "./Components/ResetPasswordPage";
import ThankYouPage from "./Components/ThankYouPage";
import LoginPage from "./Components/LoginPage";
import { TGSHeader } from "./Components/TGSHeader.js";
import Sidebar from "./Components/Sidebar";
import SettingsSidebar from "./Components/SettingsSidebar";
import JobsPage from "./Components/JobsPage";
import JobDetailsPage from "./Components/JobDetailsPage";
import SettingsPage from "./Components/SettingsPage";
import ApplicantDetailsPage from "./Components/ApplicantDetailsPage";
import CandidatesListPage from "./Components/CandidatesListPage";
import UserManagementPage from "./Components/UserManagementPage";
import LandingRouter from "./Components/LandingRouter.js";
import { Personas } from "./Components/Personas.js";
import { Tasks } from "./Components/Tasks.js";
import { Timesheets } from "./Components/Timesheets.js";
import { LeaveRequests } from "./Components/LeaveRequests.js";
import { Projects } from "./Components/Projects.js";
import {
  API_GetRoleFromId,
  API_GoogleUserInfo,
  API_RestoreUserToken,
  API_UserLogout,
} from "./Assets/ApiUrlContainer.js";
import UserContext from "./Contexts/UserContext.js";
import AuthHandler from "./Components/AuthHandler.js";
import LockedUsers from "./Components/LockedUsers";
import BannedUsers from "./Components/BannedUsers";
import RegisterPage from "./Components/RegisterPage";
import ThankYouRegistration from "./Components/ThankYouRegistration";
// Admin Pages
import AdminUserManagement from "./Components/AdminUserManagement";
import FormManagementPage from "./Components/FormManagement.js";
import AdminLeaveRequests from "./Components/AdminLeaveRequests.js";
import LearningJournals from "./Components/LearningJournals.js";

function App() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const shouldRender = (allowedRoles) => allowedRoles.includes(user?.RoleId);

  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const restoreSession = async () => {
    try {
      const response = await axios.get(API_RestoreUserToken, {
        withCredentials: true,
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });

      if (response.data.payload) {
        // console.log("Restored User Session:", response.data.payload);

        let restoredUser = {
          Email: response.data.payload.Email,
          FirstName: response.data.payload.FirstName ?? "Unknown",
          LastName: response.data.payload.LastName ?? "User",
          IsGoogleUser: response.data.payload.IsGoogleUser,
          AccountPictureUrl: response.data.payload.AccountPictureUrl,
          Id: response.data.payload.Id,
          CandidateId: response.data.payload.CandidateId,
        };

        setUserLoginCredentials(restoredUser, response.data.payload.Role);
      }
    } catch (error) {
      console.error("No active session found:", error.response?.data);
      setUser(null);
    }
  };

  const setUserLoginCredentials = async (user, role) => {
    try {
      const response = await axios.get(`${API_GetRoleFromId}/${role}`);
      const roleName = response.data.Role.toLowerCase();

      setIsAdmin(roleName === "admin" || roleName === "tgs-admin");
      setUser({
        ...user,
        RoleId: role,
        IsAdmin: roleName === "admin" || roleName === "tgs-admin",
        Role: roleName,
      });
    } catch (error) {
      console.error("Can't set admin status");
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    restoreSession();
  }, [API_RestoreUserToken]);

  useEffect(() => {
    const sid = sessionStorage.getItem("sid");
    const publicPaths = [
      "/", // login
      "/forgot-password",
      "/reset-password",
      "/register",
      "/thank-you",
      "/thank-you-registration",
    ];
    const current = window.location.pathname;

    if (!sid && !publicPaths.includes(current)) {
      window.location.replace("/");
    }
  }, []);

  const handleGoogleLoginSuccess = async (token) => {
    try {
      setGoogleLoading(true);

      const accountToken = jwtDecode(token);
      const Email = accountToken.email;
      const Picture = accountToken.picture;
      let FirstName = accountToken.name.split(" ")[0] || "";
      let LastName = accountToken.name.split(" ")[1] || "";

      if (FirstName)
        FirstName =
          FirstName[0].toUpperCase() + FirstName.slice(1).toLowerCase();
      if (LastName)
        LastName = LastName[0].toUpperCase() + LastName.slice(1).toLowerCase();

      // per-tab sid (clears on tab close)
      const sid =
        (crypto?.randomUUID && crypto.randomUUID()) ||
        Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem("sid", sid);

      // Include sid in the Google login call
      const response = await axios.post(
        API_GoogleUserInfo,
        { Email, FirstName, LastName, Picture, sid },
        { withCredentials: true }
      );

      if (response.status === 200) {
        console.log("Google login successful, session set.");
      }

      const googleUser = response.data.User;
      const googleUserRole = response.data.Role;
      const formattedRole =
        typeof googleUserRole === "string"
          ? googleUserRole.toLowerCase()
          : googleUserRole;

      const formattedGoogleUser = {
        AccountPictureUrl: googleUser.CustomAccountPictureUrl
          ? googleUser.CustomAccountPictureUrl
          : googleUser.AccountPictureUrl,
        Email: googleUser.Email,
        FirstName: googleUser.FirstName,
        LastName: googleUser.LastName,
        Id: googleUser.Id,
        RoleId: googleUser.RoleId,
        CandidateId: googleUser.CandidateId,
        IsGoogleUser: true,
      };

      setUserLoginCredentials(formattedGoogleUser, formattedRole);
      setGoogleLoading(false);
    } catch (error) {
      console.error("Error in Google Login:", error);
      setGoogleLoading(false);
    }
  };

  const handleCustomLoginSuccess = (data) => {
    const userRole =
      typeof data.Role === "string" ? data.Role.toLowerCase() : data.Role;

    // Log received user data
    console.log("Received User Data from Backend:", data);

    let loggedInUser = {
      Email: data.Email || "",
      FirstName: data.FirstName ?? "Unknown",
      LastName: data.LastName ?? "User",
      IsGoogleUser: false,
      Id: data.Id,
      CandidateId: data.CandidateId,
    };

    // Ensure user data is properly set
    setUserLoginCredentials(loggedInUser, userRole);

    // Log updated state
    console.log("Updated User State:", {
      Email: data.Email,
      FirstName: data.FirstName,
      LastName: data.LastName,
      Role: userRole,
    });
  };

  const handleLogout = async () => {
    try {
      sessionStorage.removeItem("sid");
      await axios.post(API_UserLogout, {}, { withCredentials: true });
      googleLogout();
      setUser(null);
      setIsAdmin(false);
    } catch (error) {
      console.error("Error during logout:", error.message);
    }
  };

  return (
    <GoogleOAuthProvider
      clientId={clientId}
      onScriptLoadSuccess={() => console.log("Google OAuth Loaded")}
    >
      <UserContext.Provider value={{ user, setUser }}>
        <Router>
          <AuthHandler />

          {!user ? (
            <>
              <TGSHeader />
              <Routes>
                <Route
                  path="/forgot-password"
                  element={<ForgotPasswordPage />}
                />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/thank-you" element={<ThankYouPage />} />
                <Route
                  path="/thank-you-registration"
                  element={<ThankYouRegistration />}
                />
                <Route
                  path="/"
                  element={
                    <LoginPage
                      onGoogleLoginSuccess={handleGoogleLoginSuccess}
                      onCustomLoginSuccess={handleCustomLoginSuccess}
                      googleIsLoading={googleLoading}
                    />
                  }
                />
              </Routes>
            </>
          ) : (
            <>
              <Routes>
                {/* Layout with Sidebar */}
                <Route
                  path="/*"
                  element={
                    <div className="tgs-content-container">
                      <Sidebar handleLogout={handleLogout} />
                      <div className="tgs-content-background">
                        <Routes>
                          <Route path="jobs" element={<JobsPage />} />
                          <Route path="jobs/:id" element={<JobDetailsPage />} />

                          {!shouldRender([2]) && (
                            <>
                              <Route path="candidates" element={<CandidatesListPage />} />
                              <Route path="applicants/:applicantId" element={<ApplicantDetailsPage />} />
                            </>
                          )}

                          {shouldRender([1, 4, 2]) && (
                            <>
                              <Route path="forms" element={<FormManagementPage />} />
                              <Route path="learning-journals" element={<LearningJournals />} />
                            </>
                          )}

                          {shouldRender([1, 4]) && (
                            <>
                              <Route path="personas" element={<Personas />} />
                              <Route path="projects" element={<Projects isAdmin={isAdmin} />} />
                            </>
                          )}

                          <Route path="/tasks" element={<Tasks />}>
                            <Route index element={<Navigate to="timesheets" replace />} />
                            <Route path="timesheets" element={<Timesheets />} />
                            <Route path="leaverequests" element={<LeaveRequests />} />

                            {shouldRender([1, 4]) && (
                              <Route path="admin-leave-requests" element={<AdminLeaveRequests />} />
                            )}
                          </Route>

                          <Route
                            index
                            element={<LandingRouter user={user} />}
                          />
                        </Routes>
                      </div>
                    </div>
                  }
                />

                {/* Admin Pages with Settings Sidebar */}
                <Route
                  path="/settings/*"
                  element={
                    <div style={{ display: "flex" }}>
                      <SettingsSidebar isAdmin={isAdmin} />
                      <div style={{ padding: "20px", width: "100%" }}>
                        <Routes>
                          <Route path="/" element={<SettingsPage />} />
                          {shouldRender([1, 4]) && (
                            <>
                              <Route
                                path="user-management"
                                element={<UserManagementPage />}
                              />
                              <Route
                                path="admin-users"
                                element={<AdminUserManagement />}
                              />
                              <Route
                                path="locked-users"
                                element={<LockedUsers />}
                              />
                              <Route
                                path="banned-users"
                                element={<BannedUsers />}
                              />
                            </>
                          )}

                        </Routes>
                      </div>
                    </div>
                  }
                />
              </Routes>
            </>
          )}
        </Router>
      </UserContext.Provider>
    </GoogleOAuthProvider>
  );
}

export default App;
